// hooks/useCandidates.js - Fixed pagination issues
import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';

export const useCandidates = (navigate, setAuthError) => {
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchResults, setSearchResults] = useState(null);
    const [totalResults, setTotalResults] = useState(0);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        hasNextPage: false,
        hasPreviousPage: false
    });

    const CANDIDATES_PER_PAGE = 30;

    // UNIFIED API FUNCTION - Always uses POST with request body
    const fetchCandidatesAPI = async (page = 1, searchQuery = '', advancedFilters = {}) => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            throw new Error('Authentication token missing');
        }

        // Build the request payload - ALWAYS use request body approach
        const requestBody = {
            page: page,
            limit: CANDIDATES_PER_PAGE
        };

        // Add search query if provided
        if (searchQuery && searchQuery.trim()) {
            requestBody.search = searchQuery.trim();
        }

        // Add advanced filters if any are active
        const hasAdvancedFilters = advancedFilters && Object.values(advancedFilters).some(filters => 
            Array.isArray(filters) && filters.length > 0
        );

        if (hasAdvancedFilters) {
            requestBody.filters = {};
            
            // Add each filter type that has values
            if (advancedFilters.skills && advancedFilters.skills.length > 0) {
                requestBody.filters.skills = advancedFilters.skills;
            }
            if (advancedFilters.locations && advancedFilters.locations.length > 0) {
                requestBody.filters.locations = advancedFilters.locations;
            }
            if (advancedFilters.companies && advancedFilters.companies.length > 0) {
                requestBody.filters.companies = advancedFilters.companies;
            }
            if (advancedFilters.notice_periods && advancedFilters.notice_periods.length > 0) {
                requestBody.filters.notice_periods = advancedFilters.notice_periods;
            }
        }

        //console.log('🔍 API Request:', requestBody);

        const response = await fetch('https://n8n.acespireconsulting.com/webhook/get-candidates', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(requestBody)
        });

        if (response.status === 401 || response.status === 403) {
            setAuthError('Authentication failed - please login again');
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUser');
            navigate('/login', { replace: true });
            throw new Error('Authentication failed');
        }

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const responseText = await response.text();
        if (!responseText || responseText.trim() === '') {
            return { candidates: [], pagination: { hasNextPage: false, totalRecords: 0 } };
        }

        const data = JSON.parse(responseText);
        let candidatesData = [];
        let paginationData = { hasNextPage: false, totalRecords: 0 };

        if (Array.isArray(data) && data.length > 0 && data[0]) {
            const responseObj = data[0];
            if (Array.isArray(responseObj.data)) {
                candidatesData = responseObj.data;
                paginationData = responseObj.pagination || { hasNextPage: false, totalRecords: 0 };
            }
        }

        return {
            candidates: candidatesData,
            pagination: paginationData
        };
    };

    // SIMPLIFIED loadCandidates function
    const loadCandidates = useCallback(async (page = 1, searchQuery = '', advancedFilters = {}) => {
        const isInitialLoad = page === 1 && !searchQuery && (!advancedFilters || Object.keys(advancedFilters).length === 0);
        const hasSearch = searchQuery && searchQuery.trim().length > 0;
        const hasAdvancedFilters = advancedFilters && Object.values(advancedFilters).some(filters => 
            Array.isArray(filters) && filters.length > 0
        );

        if (isInitialLoad) {
            setLoading(true);
        } else if (hasSearch || hasAdvancedFilters) {
            setSearchLoading(true);
        }

        try {
            //console.log(`🔄 Loading page ${page}${hasSearch ? ` with search: "${searchQuery}"` : ''}${hasAdvancedFilters ? ' with advanced filters' : ''}...`);
            
            // Use the unified API function
            const pageData = await fetchCandidatesAPI(page, searchQuery, advancedFilters);

            if (pageData) {
                const candidatesData = pageData.candidates;
                const paginationData = pageData.pagination;

                // Update candidates state
                setCandidates(candidatesData);

                // Update pagination state
                setPagination({
                    currentPage: paginationData.currentPage || page,
                    totalPages: paginationData.totalPages || 1,
                    totalRecords: paginationData.totalRecords || 0,
                    hasNextPage: paginationData.hasNextPage || false,
                    hasPreviousPage: paginationData.hasPreviousPage || false
                });

                // console.log('📊 Pagination state updated:', {
                //     currentPage: paginationData.currentPage || page,
                //     totalPages: paginationData.totalPages || 1,
                //     totalRecords: paginationData.totalRecords || 0
                // });

                // Handle search results
                if (hasSearch || hasAdvancedFilters) {
                    setSearchResults(candidatesData);
                    setTotalResults(paginationData.totalRecords || 0);
                } else {
                    setSearchResults(null);
                    setTotalResults(paginationData.totalRecords || 0);
                }

               // console.log(`✅ Loaded ${candidatesData.length} candidates (Page ${page})`);
            }

        } catch (error) {
            console.error('❌ Error loading candidates:', error);
            setAuthError('Failed to load candidates: ' + error.message);
            setCandidates([]);
            if (hasSearch || hasAdvancedFilters) {
                setSearchResults([]);
            }
        } finally {
            setLoading(false);
            setSearchLoading(false);
        }
    }, [navigate, setAuthError]);

    // SIMPLIFIED fetchCandidates - just calls loadCandidates
    const fetchCandidates = useCallback(async (searchQuery = '', page = 1, advancedFilters = {}) => {
        await loadCandidates(page, searchQuery, advancedFilters);
    }, [loadCandidates]);

    // Clear search function
    const clearSearch = useCallback(() => {
        setSearchResults(null);
        setSearchLoading(false);
        // Load page 1 without search or filters
        loadCandidates(1, '', {});
    }, [loadCandidates]);

    // DELETE ALL CANDIDATES
    const deleteAllCandidates = useCallback(async () => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            setAuthError('Authentication expired');
            navigate('/login', { replace: true });
            return false;
        }

        try {
            const response = await fetch('https://n8n.acespireconsulting.com/webhook/delete-all-candidates', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    confirm: 'DELETE_ALL_CANDIDATES'
                })
            });

            if (response.status === 401 || response.status === 403) {
                setAuthError('Authentication failed during delete all');
                localStorage.removeItem('adminToken');
                localStorage.removeItem('adminUser');
                navigate('/login', { replace: true });
                return false;
            }

            if (response.ok) {
                toast.success('All candidates have been deleted successfully from the database.', {
                    position: "top-right",
                    autoClose: 4000,
                });

                setCandidates([]);
                setSearchResults(null);
                setTotalResults(0);
                setPagination({
                    currentPage: 1,
                    totalPages: 1,
                    totalRecords: 0,
                    hasNextPage: false,
                    hasPreviousPage: false
                });

                setTimeout(() => {
                    loadCandidates(1, '', {});
                }, 1000);

                return true;
            } else {
                let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                try {
                    const errorText = await response.text();
                    if (errorText.trim()) {
                        try {
                            const errorJson = JSON.parse(errorText);
                            errorMessage = errorJson.error || errorJson.message || errorMessage;
                        } catch {
                            errorMessage = errorText.length > 200 ? `${errorText.substring(0, 200)}...` : errorText;
                        }
                    }
                } catch {
                    // Use default error message
                }
                throw new Error(errorMessage);
            }

        } catch (error) {
            toast.error(`Failed to delete all candidates: ${error.message}`, {
                position: "top-right",
                autoClose: 5000,
            });
            return false;
        }
    }, [navigate, setAuthError, loadCandidates]);

    // DELETE REJECTED CANDIDATES
    const deleteRejectedCandidates = useCallback(async () => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            setAuthError('Authentication expired');
            navigate('/login', { replace: true });
            return false;
        }

        try {
            const response = await fetch('https://n8n.acespireconsulting.com/webhook/delete-rejected-candidates', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    confirm: 'DELETE_REJECTED_CANDIDATES',
                    limit: 1000
                })
            });

            if (response.status === 401 || response.status === 403) {
                setAuthError('Authentication failed during delete rejected');
                localStorage.removeItem('adminToken');
                localStorage.removeItem('adminUser');
                navigate('/login', { replace: true });
                return false;
            }

            if (response.ok) {
                toast.success('All rejected candidates have been deleted successfully from the database.', {
                    position: "top-right",
                    autoClose: 4000,
                });

                // Refresh current view with current context
                setTimeout(() => {
                    if (searchResults !== null) {
                        // We're in search mode, but we don't have access to current search params here
                        // This is a limitation - consider storing search context in state
                        loadCandidates(1, '', {});
                    } else {
                        loadCandidates(pagination.currentPage || 1, '', {});
                    }
                }, 1000);

                return true;
            } else {
                let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                try {
                    const errorText = await response.text();
                    if (errorText.trim()) {
                        try {
                            const errorJson = JSON.parse(errorText);
                            errorMessage = errorJson.error || errorJson.message || errorMessage;
                        } catch {
                            errorMessage = errorText.length > 200 ? `${errorText.substring(0, 200)}...` : errorText;
                        }
                    }
                } catch {
                    // Use default error message
                }
                throw new Error(errorMessage);
            }

        } catch (error) {
            toast.error(`Failed to delete rejected candidates: ${error.message}`, {
                position: "top-right",
                autoClose: 5000,
            });
            return false;
        }
    }, [navigate, setAuthError, loadCandidates, searchResults, pagination.currentPage]);

    return {
        candidates,
        setCandidates,
        loading,
        searchLoading,
        searchResults,
        setSearchResults,
        totalResults,
        setTotalResults,
        pagination,
        setPagination,
        CANDIDATES_PER_PAGE,
        loadCandidates,
        fetchCandidates,
        clearSearch,
        deleteAllCandidates,
        deleteRejectedCandidates
    };
};